import { EnvironmentCheckReport } from '../../../modules/environment-checker/types';
import { CertificationType } from '../../../types/certifications/Certification';
import {
  CRITICAL_CHECK_IDS,
  FOCUS_LOST_LIMIT,
  GUARD_STORAGE_TTL_MS,
} from './constants';
import {
  CriticalFailureReason,
  CriticalFailureCounts,
  ExamEntryBlockReason,
  ExamFlowPhase,
  ExamGuardState,
  ExamValidationSummary,
  ValidationIncident,
} from './types';

function requiresCameraMonitoring(monitoringType?: CertificationType | null) {
  return monitoringType === 'video' || monitoringType === 'video_liveness';
}

function requiresIdentityVerification(checkIdentity?: boolean) {
  return checkIdentity === true;
}

function requiresMonitoringReady(monitoringType?: CertificationType | null) {
  return (
    monitoringType === 'video' ||
    monitoringType === 'video_liveness' ||
    monitoringType === 'fiscal'
  );
}

function isMonitoringTypeResolved(monitoringType?: CertificationType | null) {
  return Boolean(monitoringType && monitoringType !== 'select');
}

function usesUnifiedInfractionLimit(reason: CriticalFailureReason) {
  return (
    reason === 'browser_focus_lost' ||
    reason === 'monitoring_failure' ||
    reason === 'extra_monitor' ||
    reason === 'screen_share_stopped'
  );
}

function getUnifiedInfractionCount(counts: CriticalFailureCounts) {
  return (
    counts.browser_focus_lost +
    counts.monitoring_failure +
    counts.extra_monitor +
    counts.screen_share_stopped
  );
}

export function createEmptyCriticalFailureCounts(): CriticalFailureCounts {
  return {
    extra_monitor: 0,
    screen_share: 0,
    virtual_display: 0,
    camera_stream_lost: 0,
    virtual_camera_in_use: 0,
    browser_focus_lost: 0,
    monitoring_failure: 0,
    device_changed: 0,
    screen_share_stopped: 0,
  };
}

export function ensureCriticalFailureCounts(
  criticalFailureCounts?: Partial<CriticalFailureCounts> | null,
): CriticalFailureCounts {
  return {
    ...createEmptyCriticalFailureCounts(),
    ...criticalFailureCounts,
  };
}

export function getCriticalFailureReasonLabel(reason: CriticalFailureReason) {
  switch (reason) {
    case 'extra_monitor':
      return 'Monitor extra detectado';
    case 'screen_share':
      return 'Compartilhamento de tela detectado';
    case 'virtual_display':
      return 'Tela virtual detectada';
    case 'camera_stream_lost':
      return 'Falha de câmera';
    case 'virtual_camera_in_use':
      return 'Câmera virtual em uso';
    case 'browser_focus_lost':
      return 'Perda de foco';
    case 'monitoring_failure':
      return 'Desafio de liveness não validado';
    case 'screen_share_stopped':
      return 'Compartilhamento de tela interrompido';
    default:
      return 'Alteração crítica no dispositivo';
  }
}

export function getCriticalFailureResolutionHint(
  reason: CriticalFailureReason,
) {
  switch (reason) {
    case 'extra_monitor':
      return 'Mantenha apenas um monitor conectado antes de revalidar.';
    case 'camera_stream_lost':
      return 'Reposicione a webcam e confirme que o vídeo continua ativo.';
    case 'virtual_camera_in_use':
      return 'Selecione uma webcam física antes de revalidar.';
    case 'browser_focus_lost':
      return 'Retorne para a aba da prova e mantenha o navegador visível.';
    case 'monitoring_failure':
      return 'Reposicione-se diante da câmera e conclua a validação do liveness.';
    case 'screen_share_stopped':
      return 'Retome o compartilhamento da tela inteira para continuar a prova.';
    default:
      return 'Corrija a condição crítica e revalide para seguir.';
  }
}

function getCriticalFailureLimit(
  reason: CriticalFailureReason,
  focusLossLimit = FOCUS_LOST_LIMIT,
) {
  if (usesUnifiedInfractionLimit(reason)) return focusLossLimit;
  return 1;
}

export function getEntryBlockReason(
  state: ExamGuardState,
): ExamEntryBlockReason {
  if (
    requiresIdentityVerification(state.checkIdentity) &&
    !state.identityVerified
  ) {
    return 'identity_verification_pending';
  }

  if (!state.requirementsAccepted) return 'requirements_pending';
  if (state.environmentCheck !== 'approved') return 'environment_pending';
  if (!isMonitoringTypeResolved(state.monitoringType)) {
    return 'monitoring_not_ready';
  }
  if (
    requiresCameraMonitoring(state.monitoringType) &&
    state.mediaPermission !== 'preview_ready'
  ) {
    return 'media_permission_pending';
  }

  if (requiresMonitoringReady(state.monitoringType) && !state.monitoringReady) {
    return 'monitoring_not_ready';
  }

  if (state.openIncident) return 'incident_open';
  if (
    state.inExamValidationStatus === 'critical' ||
    state.inExamValidationStatus === 'terminated'
  ) {
    return 'validation_failed';
  }

  return null;
}

export function deriveGuardState(state: ExamGuardState): ExamGuardState {
  const criticalFailureCounts = ensureCriticalFailureCounts(
    state.criticalFailureCounts,
  );
  const focusLossLimit = state.focusLossLimit ?? FOCUS_LOST_LIMIT;
  const focusLossCount = getUnifiedInfractionCount(criticalFailureCounts);
  const remainingFocusAllowance = Math.max(focusLossLimit - focusLossCount, 0);
  const entryBlockReason = getEntryBlockReason(state);
  const canEnterExam = entryBlockReason === null;
  const canContinueExam =
    canEnterExam &&
    state.inExamValidationStatus !== 'critical' &&
    state.inExamValidationStatus !== 'terminated';
  const canFinishExam =
    canContinueExam &&
    state.openIncident === null &&
    state.inExamValidationStatus !== 'recovering';

  return {
    ...state,
    criticalFailureCounts,
    focusLossCount,
    focusLossLimit,
    remainingFocusAllowance,
    canEnterExam,
    canContinueExam,
    canFinishExam,
    entryBlockReason,
  };
}

export function areGuardStatesEqual(
  leftState: ExamGuardState | null | undefined,
  rightState: ExamGuardState | null | undefined,
) {
  if (!leftState || !rightState) return leftState === rightState;

  return JSON.stringify(leftState) === JSON.stringify(rightState);
}

export function createInitialGuardState(
  certificationLogId: string,
  monitoringType?: CertificationType | null,
  checkIdentity?: boolean,
  focusLossLimit = FOCUS_LOST_LIMIT,
): ExamGuardState {
  return deriveGuardState({
    certificationLogId,
    monitoringType,
    checkIdentity,
    environmentCheck: 'idle',
    requirementsAccepted: false,
    mediaPermission: 'idle',
    monitoringReady: false,
    identityVerified: false,
    inExamValidationStatus: 'idle',
    openIncident: null,
    incidents: [],
    criticalFailureCounts: createEmptyCriticalFailureCounts(),
    canEnterExam: false,
    canContinueExam: false,
    canFinishExam: false,
    focusLossCount: 0,
    focusLossLimit,
    remainingFocusAllowance: focusLossLimit,
    entryBlockReason: 'requirements_pending',
  });
}

export function getPendingExamPhase(state: ExamGuardState): ExamFlowPhase {
  if (
    requiresIdentityVerification(state.checkIdentity) &&
    !state.identityVerified
  ) {
    return 'identity_verification';
  }

  if (!state.requirementsAccepted) return 'requirements';
  if (state.environmentCheck !== 'approved') return 'environment';
  if (!isMonitoringTypeResolved(state.monitoringType)) return 'environment';

  if (
    (requiresCameraMonitoring(state.monitoringType) &&
      state.mediaPermission !== 'preview_ready') ||
    (requiresMonitoringReady(state.monitoringType) && !state.monitoringReady)
  ) {
    return 'monitoring';
  }

  return 'exam';
}

export function getPhaseIndex(phase: ExamFlowPhase) {
  return [
    'identity_verification',
    'requirements',
    'environment',
    'monitoring',
    'exam',
  ].indexOf(phase);
}

export function normalizeExamPhase(
  phase: string | null | undefined,
): ExamFlowPhase {
  if (
    phase === 'environment' ||
    phase === 'requirements' ||
    phase === 'monitoring' ||
    phase === 'identity_verification' ||
    phase === 'exam'
  ) {
    return phase;
  }

  return 'environment';
}

export function getResolvedExamPhase(
  requestedPhase: ExamFlowPhase,
  pendingPhase: ExamFlowPhase,
): ExamFlowPhase {
  return getPhaseIndex(requestedPhase) > getPhaseIndex(pendingPhase)
    ? pendingPhase
    : requestedPhase;
}

export function createValidationExpirationDate() {
  return new Date(Date.now() + GUARD_STORAGE_TTL_MS).toISOString();
}

export function buildValidationSummary(
  state: ExamGuardState,
): ExamValidationSummary {
  return {
    certificationLogId: state.certificationLogId,
    monitoringType: state.monitoringType,
    environmentStatus: state.environmentCheck,
    mediaPermission: state.mediaPermission,
    monitoringReady: state.monitoringReady,
    requirementsAccepted: state.requirementsAccepted,
    inExamValidationStatus: state.inExamValidationStatus,
    lastValidatedAt: state.lastValidatedAt,
  };
}

export function applyEnvironmentApproval(
  state: ExamGuardState,
  report: EnvironmentCheckReport,
): ExamGuardState {
  // Strip non-serializable refs (e.g. _streamRef) before persisting to Redux.
  const sanitizedChecks = report.checks.map(check => {
    const sanitizedCheck = { ...check };
    delete sanitizedCheck._streamRef;
    return sanitizedCheck;
  });

  const sanitizedReport: EnvironmentCheckReport = {
    ...report,
    checks: sanitizedChecks,
  };

  return deriveGuardState({
    ...state,
    environmentCheck:
      sanitizedReport.overallStatus === 'approved' ? 'approved' : 'blocked',
    environmentReport: sanitizedReport,
    lastValidatedAt: sanitizedReport.completedAt,
    validationExpiresAt: createValidationExpirationDate(),
  });
}

export function isCriticalCheckFailure(checkId: string, status: string) {
  return status === 'failed' && CRITICAL_CHECK_IDS.has(checkId);
}

export function mapCheckIdToReason(checkId: string): CriticalFailureReason {
  switch (checkId) {
    case 'monitor_count':
      return 'extra_monitor';
    case 'screen_share':
      return 'screen_share';
    case 'virtual_display':
      return 'virtual_display';
    case 'camera_stream':
      return 'camera_stream_lost';
    case 'webcam_virtual':
      return 'virtual_camera_in_use';
    case 'browser_focus':
      return 'browser_focus_lost';
    case 'monitoring_liveness':
      return 'monitoring_failure';
    case 'screen_share_active':
      return 'screen_share_stopped';
    default:
      return 'device_changed';
  }
}

export function createIncident(
  certificationLogId: string,
  checkId: string,
  message: string,
  occurrence: number,
  perReasonAttempt?: number,
  focusLossLimit = FOCUS_LOST_LIMIT,
): ValidationIncident {
  const reason = mapCheckIdToReason(checkId);
  const limit = getCriticalFailureLimit(reason, focusLossLimit);
  const displayOccurrence = Math.min(occurrence, limit);
  const remainingAllowance = Math.max(limit - displayOccurrence, 0);
  const reasonLabel = getCriticalFailureReasonLabel(reason);
  const resolutionHint = getCriticalFailureResolutionHint(reason);
  const normalizedMessage = usesUnifiedInfractionLimit(reason)
    ? `Infração ${displayOccurrence} de ${limit}. ${message}`
    : message;

  return {
    checkId,
    status: 'open',
    severity: 'critical',
    timestamp: new Date().toISOString(),
    attempt: perReasonAttempt ?? displayOccurrence,
    occurrence: displayOccurrence,
    certificationLogId,
    reason,
    reasonLabel,
    message: normalizedMessage,
    limit,
    remainingAllowance,
    resolutionHint,
  };
}

export function resolveIncident(
  incident: ValidationIncident,
): ValidationIncident {
  return {
    ...incident,
    status: 'resolved',
    resolvedAt: new Date().toISOString(),
  };
}
